import { createTheme, alpha } from '@mui/material/styles';

// ============================================================
// TradeWars — War-Themed Color Palette
//
// Primary:   Crimson Red  — power, battle, dominance
// Secondary: Burnished Gold — victory, valor, reward
// Neutral:   Steel Silver  — armor, strength, precision
// Dark:      Charcoal Black — night, steel, grit
// ============================================================

export const BRAND_COLORS = {
  // Reds — primary brand color
  crimson:      '#B71C1C',   // deep crimson — primary main
  crimsonLight: '#E53935',   // bright battle red — hover states
  crimsonDark:  '#7F0000',   // blood red — pressed/dark
  scarlet:      '#C62828',   // mid crimson

  // Golds — secondary / accent
  gold:         '#C9A84C',   // burnished gold — secondary main
  goldLight:    '#FFD54F',   // bright gold — highlights
  goldDark:     '#8D6E1A',   // antique gold — dark accent
  amber:        '#FFD54F',   // vivid amber — CTAs, badges

  // Silvers — neutral / surfaces
  silver:       '#C0C0C0',   // classic silver
  silverLight:  '#E8E8E8',   // light silver — backgrounds
  silverDark:   '#9E9E9E',   // steel grey
  steel:        '#607D8B',   // blue-steel

  // Darks — backgrounds, navbars
  charcoal:     '#1A1A1A',   // near-black steel
  ironBlack:    '#111111',   // footer, deep backgrounds
  gunmetal:     '#2C2C2E',   // card surfaces on dark bg
  slate:        '#37474F',   // dark slate blue-grey

  // Text
  textOnDark:   '#F5F0E8',   // warm off-white — text on dark bg
  textOnLight:  '#1A1A1A',   // near-black — text on light bg
  textMuted:    '#9E9E9E',   // muted silver text
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main:        BRAND_COLORS.crimson,
      light:       BRAND_COLORS.crimsonLight,
      dark:        BRAND_COLORS.crimsonDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:        BRAND_COLORS.gold,
      light:       BRAND_COLORS.goldLight,
      dark:        BRAND_COLORS.goldDark,
      contrastText: BRAND_COLORS.ironBlack,
    },
    error: {
      main: '#D32F2F',
    },
    warning: {
      main: BRAND_COLORS.amber,
    },
    success: {
      main: '#2E7D32',
    },
    info: {
      main: BRAND_COLORS.steel,
    },
    background: {
      default: '#F8F6F3',   // warm off-white — feels like aged parchment
      paper:   '#FFFFFF',
    },
    text: {
      primary:   BRAND_COLORS.textOnLight,
      secondary: '#555555',
      disabled:  BRAND_COLORS.textMuted,
    },
    divider: '#DDD8D0',

    // Custom palette slots used throughout the app
    brand: {
      crimson:   BRAND_COLORS.crimson,
      gold:      BRAND_COLORS.gold,
      silver:    BRAND_COLORS.silver,
      charcoal:  BRAND_COLORS.charcoal,
      ironBlack: BRAND_COLORS.ironBlack,
      amber:     BRAND_COLORS.amber,
      steel:     BRAND_COLORS.steel,
    },
  },

  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontFamily: '"Oswald", "Impact", "Arial Black", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      textTransform: 'uppercase',
    },
    h2: {
      fontFamily: '"Oswald", "Impact", "Arial Black", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Oswald", "Impact", "Arial Black", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontWeight: 700,
      fontFamily: '"Oswald", "Arial Black", sans-serif',
    },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: {
      fontWeight: 700,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },

  shape: {
    borderRadius: 4,   // sharper corners — more military feel
  },

  components: {
    // ── Buttons ──────────────────────────────────────────────
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          padding: '10px 24px',
          transition: 'all 0.2s ease',
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${BRAND_COLORS.crimsonLight} 0%, ${BRAND_COLORS.crimson} 50%, ${BRAND_COLORS.crimsonDark} 100%)`,
          boxShadow: `0 4px 15px ${alpha(BRAND_COLORS.crimson, 0.4)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${BRAND_COLORS.crimson} 0%, ${BRAND_COLORS.crimsonDark} 100%)`,
            boxShadow: `0 6px 20px ${alpha(BRAND_COLORS.crimson, 0.6)}`,
            transform: 'translateY(-1px)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(135deg, ${BRAND_COLORS.goldLight} 0%, ${BRAND_COLORS.gold} 50%, ${BRAND_COLORS.goldDark} 100%)`,
          color: BRAND_COLORS.ironBlack,
          boxShadow: `0 4px 15px ${alpha(BRAND_COLORS.gold, 0.4)}`,
          '&:hover': {
            background: `linear-gradient(135deg, ${BRAND_COLORS.gold} 0%, ${BRAND_COLORS.goldDark} 100%)`,
            boxShadow: `0 6px 20px ${alpha(BRAND_COLORS.gold, 0.5)}`,
            transform: 'translateY(-1px)',
          },
        },
        outlinedPrimary: {
          borderColor: BRAND_COLORS.crimson,
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: alpha(BRAND_COLORS.crimson, 0.08),
          },
        },
      },
    },

    // ── Cards ────────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
          border: '1px solid #DDD8D0',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: `0 8px 24px rgba(0,0,0,0.15)`,
          },
        },
      },
    },

    // ── AppBar (Navigation) ──────────────────────────────────
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(90deg, ${BRAND_COLORS.ironBlack} 0%, ${BRAND_COLORS.charcoal} 100%)`,
          boxShadow: `0 2px 20px rgba(0,0,0,0.5)`,
          borderBottom: `2px solid ${BRAND_COLORS.crimson}`,
        },
      },
    },

    // ── Chip ─────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 600,
        },
        colorPrimary: {
          backgroundColor: alpha(BRAND_COLORS.crimson, 0.12),
          color: BRAND_COLORS.crimson,
          borderColor: BRAND_COLORS.crimson,
        },
        colorSecondary: {
          backgroundColor: alpha(BRAND_COLORS.gold, 0.15),
          color: BRAND_COLORS.goldDark,
          borderColor: BRAND_COLORS.gold,
        },
      },
    },

    // ── TextField ────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND_COLORS.crimsonLight,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: BRAND_COLORS.crimson,
          },
        },
      },
    },

    // ── Stepper ──────────────────────────────────────────────
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: BRAND_COLORS.silverDark,
          '&.Mui-active': { color: BRAND_COLORS.crimson },
          '&.Mui-completed': { color: BRAND_COLORS.gold },
        },
      },
    },

    // ── LinearProgress ───────────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: alpha(BRAND_COLORS.crimson, 0.15) },
        bar: {
          background: `linear-gradient(90deg, ${BRAND_COLORS.crimson}, ${BRAND_COLORS.gold})`,
        },
      },
    },

    // ── Tabs ─────────────────────────────────────────────────
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          letterSpacing: '0.04em',
          '&.Mui-selected': { color: BRAND_COLORS.crimson },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: BRAND_COLORS.crimson, height: 3 },
      },
    },

    // ── Badge ────────────────────────────────────────────────
    MuiBadge: {
      styleOverrides: {
        colorPrimary: { backgroundColor: BRAND_COLORS.crimson },
        colorSecondary: { backgroundColor: BRAND_COLORS.gold, color: BRAND_COLORS.ironBlack },
      },
    },
  },
});

export default theme;
